import { useEffect, useState } from 'react';
import firebase, { db } from '../firebaseConfig';

// ---- Data model ------------------------------------------------------------
// Amalfi House renovation domain. A "space" is one renovation zone (Bedroom 2,
// Pool, Front Yard, …):
//
// spaces/{id}            : name, category ('interior'|'outdoor'|'exterior'),
//                          status ('planning'|'in-progress'|'done'), budget,
//                          notes, sortOrder, s3Prefix ('spaces/<id>/'),
//                          assetCount, totalBytes, createdAt
// spaces/{id}/assets     : name, sizeBytes, contentType, kind, s3Key, status,
//                          meta { status: 'queued'|'running'|'done'|'error' },
//                          lineage {…}, uploadedAt      ← written by uploads,
//                          processed by the backend meta worker
// spaces/{id}/uploads    : live guest-upload progress rows (mirrored by the
//                          backend from drop-link heartbeats)
// spaces/{id}/moodboards : title, tiles[{id,type:'asset'|'url',s3Key?,url?,note}]
// spaces/{id}/lists      : title, type ('furniture'|'todo'|'shopping'),
//                          entries[{id,name,link,price,qty,status,done}]
// dropLinks/{token}      : guest upload links (field names keep the kit's
//                          itemId/itemName shape — the backend reads them)
// jobs/{id}              : fan-out job state + jobs/{id}/tasks telemetry
// jobControls/{jobId}    : { action: 'cancel'|'pause'|'resume' } — kill switches
// config/app             : live-tunable backend settings
// configs/app            : backend-written status (online flag, heartbeat)
// -----------------------------------------------------------------------------

const ready = () => firebase.apps.length > 0 && db;
const now = () => firebase.firestore.FieldValue.serverTimestamp();

export function useCollection(path, { orderBy, dir = 'desc' } = {}) {
  const [docs, setDocs] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!ready()) {
      setDocs([]);
      return undefined;
    }
    let ref = db.collection(path);
    if (orderBy) ref = ref.orderBy(orderBy, dir);
    const unsub = ref.onSnapshot(
      (snap) => setDocs(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (err) => {
        setError(err.message);
        setDocs([]);
      }
    );
    return () => unsub();
  }, [path, orderBy, dir]);

  return { docs, error };
}

export function useDoc(path) {
  const [doc, setDoc] = useState(undefined);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!ready() || !path) return undefined;
    const unsub = db.doc(path).onSnapshot(
      (snap) => setDoc(snap.exists ? { id: snap.id, ...snap.data() } : null),
      (err) => setError(err.message)
    );
    return () => unsub();
  }, [path]);

  return { doc, error };
}

// ---- Spaces (renovation zones) ------------------------------------------------
export const useSpaces = () => useCollection('spaces', { orderBy: 'sortOrder', dir: 'asc' });
export const useSpace = (id) => useDoc(id ? `spaces/${id}` : null);

export function useItemAssets(spaceId) {
  return useCollection(spaceId ? `spaces/${spaceId}/assets` : 'spaces', {
    orderBy: 'uploadedAt',
  });
}

// Live, in-flight uploads (mirrored by the backend from drop-link guests'
// progress heartbeats). Lets the owner watch a contributor's upload fill in
// before the finished file lands as a real asset.
export function useItemUploads(spaceId) {
  return useCollection(spaceId ? `spaces/${spaceId}/uploads` : 'spaces', {
    orderBy: 'updatedMs',
  });
}

// Dismiss a stale/abandoned in-progress upload row from the owner's view.
export async function deleteItemUpload(spaceId, id) {
  if (!ready() || !spaceId || !id) return;
  await db.collection('spaces').doc(spaceId).collection('uploads').doc(id).delete().catch(() => {});
}

export async function createSpace({ id, name, category = 'interior', icon = '', description = '', sortOrder = 999 }) {
  const ref = id ? db.collection('spaces').doc(id) : db.collection('spaces').doc();
  await ref.set({
    name: name || 'Untitled',
    category,
    icon,
    description,
    status: 'planning',
    budget: 0,
    notes: '',
    sortOrder,
    s3Prefix: `spaces/${ref.id}/`,
    assetCount: 0,
    totalBytes: 0,
    createdAt: now(),
  });
  return ref.id;
}

export async function updateSpace(spaceId, patch) {
  await db.collection('spaces').doc(spaceId).set(patch, { merge: true });
}

export async function deleteSpace(spaceId) {
  await db.collection('spaces').doc(spaceId).delete();
}

// ---- Moodboards -----------------------------------------------------------------
// One board = a doc with an embedded tiles[] array (boards stay small — tens of
// tiles). Tiles reference an uploaded asset (s3Key, displayed via a presigned
// URL) or an external image URL, each with an optional note.
export function useMoodboards(spaceId) {
  return useCollection(spaceId ? `spaces/${spaceId}/moodboards` : 'spaces', {
    orderBy: 'createdAt', dir: 'asc',
  });
}

export async function createMoodboard(spaceId, title) {
  const ref = db.collection('spaces').doc(spaceId).collection('moodboards').doc();
  await ref.set({ title: title || 'Moodboard', tiles: [], createdAt: now() });
  return ref.id;
}

export async function updateMoodboard(spaceId, boardId, patch) {
  await db.collection('spaces').doc(spaceId).collection('moodboards').doc(boardId)
    .set(patch, { merge: true });
}

export async function deleteMoodboard(spaceId, boardId) {
  await db.collection('spaces').doc(spaceId).collection('moodboards').doc(boardId).delete();
}

const tileId = () => `t${Date.now()}${Math.random().toString(36).slice(2, 7)}`;

export async function addMoodboardTile(spaceId, boardId, tile) {
  const clean = {
    id: tileId(),
    type: tile.type, // 'asset' | 'url'
    ...(tile.s3Key ? { s3Key: tile.s3Key } : {}),
    ...(tile.url ? { url: tile.url } : {}),
    ...(tile.name ? { name: tile.name } : {}),
    note: tile.note || '',
    addedAt: Date.now(),
  };
  await db.collection('spaces').doc(spaceId).collection('moodboards').doc(boardId).update({
    tiles: firebase.firestore.FieldValue.arrayUnion(clean),
  });
  return clean.id;
}

export async function saveMoodboardTiles(spaceId, boardId, tiles) {
  await db.collection('spaces').doc(spaceId).collection('moodboards').doc(boardId)
    .set({ tiles }, { merge: true });
}

// ---- Technical views (room pages) -----------------------------------------------
// One doc per view type at spaces/{id}/views/{viewId} — viewIds are fixed by
// the tab config in data/rooms.js (render, blueprint, dimensions, studs,
// electrical, plumbing). Same tile shape as moodboards; docs are created
// lazily on first write, so empty tabs cost nothing.
export const useSpaceView = (spaceId, viewId) =>
  useDoc(spaceId && viewId ? `spaces/${spaceId}/views/${viewId}` : null);

export async function addViewTile(spaceId, viewId, tile) {
  const clean = {
    id: tileId(),
    type: tile.type, // 'asset' | 'url'
    ...(tile.s3Key ? { s3Key: tile.s3Key } : {}),
    ...(tile.url ? { url: tile.url } : {}),
    ...(tile.name ? { name: tile.name } : {}),
    note: tile.note || '',
    addedAt: Date.now(),
  };
  await db.collection('spaces').doc(spaceId).collection('views').doc(viewId).set(
    { tiles: firebase.firestore.FieldValue.arrayUnion(clean), updatedAt: Date.now() },
    { merge: true }
  );
  return clean.id;
}

export async function saveViewTiles(spaceId, viewId, tiles) {
  await db.collection('spaces').doc(spaceId).collection('views').doc(viewId)
    .set({ tiles, updatedAt: Date.now() }, { merge: true });
}

export async function saveViewNotes(spaceId, viewId, notes) {
  await db.collection('spaces').doc(spaceId).collection('views').doc(viewId)
    .set({ notes, updatedAt: Date.now() }, { merge: true });
}

// ---- Lists (furniture / todo / shopping) ------------------------------------------
// Same shape as moodboards: one doc per list with an embedded entries[] array.
export function useLists(spaceId) {
  return useCollection(spaceId ? `spaces/${spaceId}/lists` : 'spaces', {
    orderBy: 'createdAt', dir: 'asc',
  });
}

export async function createList(spaceId, { title, type = 'todo' }) {
  const ref = db.collection('spaces').doc(spaceId).collection('lists').doc();
  await ref.set({ title: title || 'List', type, entries: [], createdAt: now() });
  return ref.id;
}

export async function deleteList(spaceId, listId) {
  await db.collection('spaces').doc(spaceId).collection('lists').doc(listId).delete();
}

export async function saveListEntries(spaceId, listId, entries) {
  await db.collection('spaces').doc(spaceId).collection('lists').doc(listId)
    .set({ entries }, { merge: true });
}

export const newListEntry = (fields = {}) => ({
  id: tileId(),
  name: '',
  link: '',
  price: 0,
  qty: 1,
  status: 'idea', // furniture: idea → ordered → delivered ; todo/shopping use `done`
  done: false,
  addedAt: Date.now(),
  ...fields,
});

// ---- Assets ------------------------------------------------------------------
// Records a finished OWNER upload as an asset (guests go through the backend's
// /drop/:token/record instead, which mirrors this shape via the admin SDK —
// keep the two in sync). Queues the backend meta worker via meta.status.
export async function recordAsset(itemId, asset) {
  const ref = db.collection('spaces').doc(itemId).collection('assets').doc();
  await ref.set({
    name: asset.name,
    sizeBytes: asset.sizeBytes || 0,
    contentType: asset.contentType || '',
    kind: asset.kind || 'file',
    s3Key: asset.s3Key,
    status: asset.status || 'stored',
    meta: { status: 'queued' },
    lineage: {
      origin: 'upload',
      by: firebase.auth().currentUser?.email || '',
      originalName: asset.name,
      addedAt: Date.now(),
      ...(asset.uploaderMeta ? { uploader: asset.uploaderMeta } : {}),
    },
    tags: Array.isArray(asset.tags) ? asset.tags.slice(0, 30) : [],
    ...(asset.folderPrefix ? { folderPrefix: asset.folderPrefix } : {}),
    ...(Array.isArray(asset.parts) ? { parts: asset.parts } : {}),
    uploadedAt: now(),
  });
  await db.collection('spaces').doc(itemId).set(
    {
      assetCount: firebase.firestore.FieldValue.increment(1),
      totalBytes: firebase.firestore.FieldValue.increment(asset.sizeBytes || 0),
    },
    { merge: true }
  );
  return ref.id;
}

export async function renameAsset(itemId, assetId, name) {
  await db.collection('spaces').doc(itemId).collection('assets').doc(assetId)
    .set({ name }, { merge: true });
}

export async function deleteAssetDoc(itemId, assetId) {
  await db.collection('spaces').doc(itemId).collection('assets').doc(assetId).delete();
}

// ---- Fan-out jobs -------------------------------------------------------------
// One job doc + a tasks subcollection with live telemetry per prep/chunk/stitch.
export function useJob(jobId) {
  const [job, setJob] = useState(undefined);
  const [tasks, setTasks] = useState([]);

  useEffect(() => {
    if (!ready() || !jobId) {
      setJob(undefined);
      setTasks([]);
      return undefined;
    }
    const unsubJob = db.collection('jobs').doc(jobId).onSnapshot(
      (snap) => setJob(snap.exists ? { id: snap.id, ...snap.data() } : null),
      () => setJob(null)
    );
    const unsubTasks = db
      .collection('jobs')
      .doc(jobId)
      .collection('tasks')
      .onSnapshot(
        (snap) => {
          const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
          items.sort((a, b) => (a.index ?? 0) - (b.index ?? 0));
          setTasks(items);
        },
        () => setTasks([])
      );
    return () => {
      unsubJob();
      unsubTasks();
    };
  }, [jobId]);

  return { job, tasks };
}

// All fan-out jobs (live + recent), newest first, for the Jobs page.
export function useJobs(max = 100, enabled = true) {
  const [jobs, setJobs] = useState(null);
  useEffect(() => {
    if (!ready() || !enabled) {
      setJobs(null);
      return undefined;
    }
    const unsub = db.collection('jobs').onSnapshot(
      (snap) => {
        const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        items.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
        setJobs(items.slice(0, max));
      },
      () => setJobs([])
    );
    return unsub;
  }, [max, enabled]);
  return jobs;
}

const JOB_ACTIVE = ['queued', 'planning', 'running', 'stitching'];
export const jobIsActive = (job) => JOB_ACTIVE.includes(job?.status) || !!job?.paused;

// Queue a fan-out job for the backend orchestrator to adopt. `type` selects the
// role handlers in backend/runJob.js; `spec` is the job's input (kept small —
// big inputs belong in S3). Example: queueFanoutJob('manifest', { prefix:
// 'spaces/abc/' }).
export async function queueFanoutJob(type, spec, { title = '' } = {}) {
  const ref = db.collection('jobs').doc(`${type}-${Date.now()}`);
  await ref.set({
    kind: 'fanout',
    type,
    spec,
    title: title || null,
    status: 'queued',
    progress: 0,
    createdAt: Date.now(),
    requestedBy: firebase.auth().currentUser?.email || '',
  });
  return ref.id;
}

// Write a control request the orchestrator watches. action: cancel|pause|resume.
// This is THE kill switch — every long-running backend job must honor it.
export async function controlJob(jobId, action) {
  if (!ready() || !jobId) return;
  await db.collection('jobControls').doc(jobId).set(
    { action, requestedAt: Date.now(), by: firebase.auth().currentUser?.email || 'admin' },
    { merge: true }
  );
}
export const cancelJob = (jobId) => controlJob(jobId, 'cancel');
export const pauseJob = (jobId) => controlJob(jobId, 'pause');
export const resumeJob = (jobId) => controlJob(jobId, 'resume');

// Delete a finished/errored job doc and its per-task telemetry, so the Jobs
// page can be tidied. (The orchestrator never touches done/error jobs, so
// removing them is safe.)
export async function deleteJob(jobId) {
  if (!ready() || !jobId) return;
  const jobRef = db.collection('jobs').doc(jobId);
  const tasks = await jobRef.collection('tasks').get();
  // Tasks subcollections are small (a few chunks + prep/stitch); one batch is
  // plenty, but chunk by 450 just in case.
  let batch = db.batch();
  let n = 0;
  for (const d of tasks.docs) {
    batch.delete(d.ref);
    n += 1;
    if (n % 450 === 0) {
      // eslint-disable-next-line no-await-in-loop
      await batch.commit();
      batch = db.batch();
    }
  }
  await batch.commit();
  await jobRef.delete();
}

// Bulk-delete every errored job. Returns the number removed.
export async function clearErroredJobs() {
  if (!ready()) return 0;
  const snap = await db.collection('jobs').where('status', '==', 'error').get();
  for (const d of snap.docs) {
    // eslint-disable-next-line no-await-in-loop
    await deleteJob(d.id);
  }
  return snap.size;
}

// ---- Guest drop links ----------------------------------------------------------
// dropLinks/{token} : itemId, itemName, label, ownerName, passcode (''|code),
//   status ('active'|'revoked'), createdAt, expiresAt (ms epoch),
//   uploadedBytes, uploadedCount, lastUploadAt, lastContributor.
// A contributor with the link uploads through the backend (token-auth), which
// records assets into the item via the admin SDK — guests never touch Firestore.
function randomToken(bytes = 18) {
  const arr = new Uint8Array(bytes);
  (window.crypto || window.msCrypto).getRandomValues(arr);
  return Array.from(arr)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

export async function createDropLink(
  item,
  { label = '', passcode = '', expiresDays = 14, guestName = '', guestNote = '' } = {}
) {
  const token = randomToken();
  const me = firebase.auth().currentUser;
  const ownerName = me?.displayName || (me?.email ? me.email.split('@')[0] : '') || '';
  await db
    .collection('dropLinks')
    .doc(token)
    .set({
      token,
      itemId: item.id,
      itemName: item.name || '',
      label: label || '',
      ownerName,
      guestName: guestName || '',
      guestNote: guestNote || '',
      passcode: passcode || '',
      status: 'active',
      createdAt: now(),
      expiresAt: Date.now() + (Number(expiresDays) || 14) * 86400000,
      uploadedBytes: 0,
      uploadedCount: 0,
    });
  return token;
}

export function useDropLinks(itemId) {
  const [links, setLinks] = useState(null);
  useEffect(() => {
    if (!ready() || !itemId) {
      setLinks([]);
      return undefined;
    }
    const unsub = db
      .collection('dropLinks')
      .where('itemId', '==', itemId)
      .onSnapshot(
        (snap) => {
          const docs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
          docs.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
          setLinks(docs);
        },
        () => setLinks([])
      );
    return unsub;
  }, [itemId]);
  return links;
}

export async function revokeDropLink(token) {
  await db.collection('dropLinks').doc(token).set({ status: 'revoked' }, { merge: true });
}

export async function reactivateDropLink(token, { expiresDays = 14 } = {}) {
  await db
    .collection('dropLinks')
    .doc(token)
    .set({ status: 'active', expiresAt: Date.now() + (Number(expiresDays) || 14) * 86400000 }, { merge: true });
}

export async function deleteDropLink(token) {
  await db.collection('dropLinks').doc(token).delete();
}

// ---- Live config (config/app) ---------------------------------------------------
// Client-editable runtime knobs the backend deep-merges over its defaults (see
// backend/shared/liveConfig.js). ANYTHING an operator may want to tune at
// runtime belongs there, never in a redeploy.
export const useLiveConfig = () => useDoc('config/app');
export async function saveLiveConfig(patch) {
  await db.collection('config').doc('app').set(patch, { merge: true });
}

// Backend-written status (online flag + heartbeat) for a health indicator.
export const useBackendStatus = () => useDoc('configs/app');

// ---- Smart-home controls (ISY bridge) --------------------------------------------
// Device state is mirrored into controlDevices by the Python bridge on the
// house LAN; the app only ever CREATES command docs — the bridge executes
// them and writes status back. Owner-only (Firestore rules).
export const CONTROLS_EMAILS = ['jitochadha@gmail.com'];
export const canUseControls = (user) =>
  !!user && CONTROLS_EMAILS.includes((user.email || '').toLowerCase());

export const useControlDevices = () => useCollection('controlDevices', { orderBy: 'name', dir: 'asc' });
export const useBridgeStatus = () => useDoc('controlDevices/_bridge');

// cmd: 'on' | 'off' | 'level' | 'query'. createdAt is client epoch ms on
// purpose — the bridge uses it to expire stale commands, so it must be a
// number, not a server timestamp sentinel.
export async function sendControlCommand(address, cmd, { level = null, email } = {}) {
  const ref = await db.collection('controlCommands').add({
    address,
    cmd,
    ...(level == null ? {} : { level }),
    requestedBy: email,
    status: 'pending',
    createdAt: Date.now(),
  });
  return ref.id;
}

export const useControlCommand = (id) => useDoc(id ? `controlCommands/${id}` : null);

// One-shot: resolve when a command doc leaves 'pending' (with its data, incl.
// any `result` the bridge wrote back — e.g. yt_search's results list), or
// null on timeout. For query-style commands where the UI needs the answer.
export function awaitCommandResult(id, { timeoutMs = 20000 } = {}) {
  return new Promise((resolve) => {
    const ref = db.collection('controlCommands').doc(id);
    let unsub = () => {};
    const timer = setTimeout(() => { unsub(); resolve(null); }, timeoutMs);
    unsub = ref.onSnapshot(
      (snap) => {
        const d = snap.data();
        if (d && d.status && d.status !== 'pending') {
          clearTimeout(timer);
          unsub();
          resolve(d);
        }
      },
      () => { clearTimeout(timer); unsub(); resolve(null); }
    );
  });
}

// iAquaLink pool/spa (Zodiac cloud, mirrored by the same bridge process).
// Pool devices live in controlDevices with vendor: 'aqualink'; commands ride
// the same queue with target: 'aqua'. cmd: 'on' | 'off' | 'setTemp' (+value).
export const useAquaBridgeStatus = () => useDoc('controlDevices/_aquaBridge');

// Network inventory (Controls -> Network tab): the LAN device roster.
// Docs carry identity/topology (ip, mac, firmware, notes); realtime liveness
// comes from the controlDevices heartbeat doc named in `heartbeatDoc`.
export const useNetworkDevices = () => useCollection('networkDevices', { orderBy: 'order', dir: 'asc' });

export async function sendAquaCommand(address, cmd, { value = null, email } = {}) {
  const ref = await db.collection('controlCommands').add({
    target: 'aqua',
    address,
    cmd,
    ...(value == null ? {} : { value }),
    requestedBy: email,
    status: 'pending',
    createdAt: Date.now(),
  });
  return ref.id;
}

// Bluetooth lights + media players (Apple TV, Sonos) via Home Assistant on
// the house box (bridge/ha_bridge.py). Devices live in controlDevices with
// vendor: 'ha'; commands ride the same queue with target: 'ha'.
// Lights: 'on' | 'off' | 'level' (0-255) | 'color' (+rgb '#rrggbb') |
// 'ct' (+kelvin). Media (kind: 'media'): 'on' | 'off' | 'play' | 'pause' |
// 'volume_up' | 'volume_down' | 'volume_set' (+volume 0-100), and Apple TV
// extras: 'app' (+app, launches a tvOS app), 'open_url' (+url, deep-links —
// Netflix search), 'remote' (+button up|down|left|right|select|menu|home),
// 'yt_play' (+query or videoId — bridge searches YouTube and plays the top
// result via the Lounge/cast API), 'yt_search' (+query — writes the top
// results onto the command doc's `result.results` for choose-mode; read it
// with awaitCommandResult), 'tv_search_all' (+query — types into tvOS's
// cross-app Search screen; needs the one-time Companion keyboard pairing:
// 'atv_pair_begin' then 'atv_pair_pin' +pin), 'yt_pair' (+code, one-time).
// The Apple TV relays on/off/volume to the TV itself over HDMI-CEC.
// Fans (kind: 'fan', Dyson purifier): 'on' | 'off' | 'fan_speed' (+pct
// 10-100) | 'set_preset' (+preset auto|manual) | 'night_mode' /
// 'oscillate' (+value on|off) | 'osc_mode' (+value from oscModes) |
// 'sleep_timer' (+minutes 0-540).
export const useHaBridgeStatus = () => useDoc('controlDevices/_haBridge');

// House DJ waveform feed (controlDevices/_djViz): the DJ deck publishes the
// pack (POST :8091/dj/viz → bridge, contract in docs/dj-stream.md). The
// Pi's ffmpeg envelope is only a fallback when the deck isn't sending.
// Callback-based rather than useDoc so the canvas can consume packets in a
// ref without a React re-render every second.
// Watch one controlCommands doc — the bridge writes `stage` strings while
// executing (DJ grouping takes seconds) and a final status/error, so the
// DJ panel can narrate progress instead of showing a dumb spinner.
export function watchCommand(id, cb) {
  if (!ready() || !id) return () => {};
  return db.collection('controlCommands').doc(id).onSnapshot(
    (snap) => {
      if (snap.exists) cb({ id: snap.id, ...snap.data() });
    },
    (err) => console.error('command watch failed', err)
  );
}

export function watchDjViz(cb) {
  if (!ready()) return () => {};
  return db.doc('controlDevices/_djViz').onSnapshot(
    (snap) => {
      if (snap.exists) cb(snap.data());
    },
    (err) => console.error('djViz watch failed', err)
  );
}

export async function sendHaCommand(address, cmd, { level = null, value = null, rgb = null, kelvin = null, volume = null, app = null, url = null, button = null, query = null, videoId = null, code = null, temp = null, mode = null, fan = null, preset = null, swing = null, pct = null, minutes = null, email } = {}) {
  const ref = await db.collection('controlCommands').add({
    target: 'ha',
    address,
    cmd,
    ...(level == null ? {} : { level }),
    ...(value == null ? {} : { value }),
    ...(rgb == null ? {} : { rgb }),
    ...(kelvin == null ? {} : { kelvin }),
    ...(volume == null ? {} : { volume }),
    ...(app == null ? {} : { app }),
    ...(url == null ? {} : { url }),
    ...(button == null ? {} : { button }),
    ...(query == null ? {} : { query }),
    ...(videoId == null ? {} : { videoId }),
    ...(code == null ? {} : { code }),
    ...(temp == null ? {} : { temp }),
    ...(mode == null ? {} : { mode }),
    ...(fan == null ? {} : { fan }),
    ...(preset == null ? {} : { preset }),
    ...(swing == null ? {} : { swing }),
    ...(pct == null ? {} : { pct }),
    ...(minutes == null ? {} : { minutes }),
    requestedBy: email,
    status: 'pending',
    createdAt: Date.now(),
  });
  return ref.id;
}

// House DJ (docs/dj-stream.md): the AI DJ deck streams into Icecast on the
// house box and the Sonos zones tune in. 'dj_set' makes the given zones
// (['ha:media_player.x', …]) the complete DJ group — zones not listed drop
// out; 'dj_off' stops the stream and ungroups everywhere.
export async function sendDjCommand(cmd, { zones = null, email } = {}) {
  const ref = await db.collection('controlCommands').add({
    target: 'ha',
    address: 'ha:dj',
    cmd,
    ...(zones ? { zones } : {}),
    requestedBy: email,
    status: 'pending',
    createdAt: Date.now(),
  });
  return ref.id;
}

// Motorized shades via the Rollease Automate Pulse 2 hub (The Shade Store),
// mirrored by bridge/shade_bridge.py on the house LAN. Devices live in
// controlDevices with vendor: 'shade'; commands ride the same queue with
// target: 'shade'. cmd: 'open' | 'close' | 'stop' | 'position' (+position
// 0-100 percent CLOSED — 0 is fully open).
export const useShadeBridgeStatus = () => useDoc('controlDevices/_shadeBridge');

// Browse rails for the Media tab's TV card: mediaFeeds/{youtube|netflix|
// disney-plus|prime-video|...} refreshed hourly by the backend
// (backend/api/mediaFeedsWorker.js). youtube = the owner's subscriptions
// (never generic trending — owner's call); others = TMDB top picks.
export const useMediaFeed = (feedId) => useDoc(feedId ? `mediaFeeds/${feedId}` : null);

export async function sendShadeCommand(address, cmd, { position = null, email } = {}) {
  const ref = await db.collection('controlCommands').add({
    target: 'shade',
    address,
    cmd,
    ...(position == null ? {} : { position }),
    requestedBy: email,
    status: 'pending',
    createdAt: Date.now(),
  });
  return ref.id;
}

// Ring cameras, mirrored by bridge/ring_snap.py on the house box (ring-mqtt
// -> MQTT -> Firestore). Docs live in controlDevices with vendor: 'ring',
// kind: 'camera': { name, locationName, snapshotB64 (data-URL JPEG),
// snapshotAt, motion, motionAt, reachable, battery?, liveUrl }. Read-only —
// live view goes browser -> go2rtc on the house box, not through Firestore.
export const useRingBridgeStatus = () => useDoc('controlDevices/_ringBridge');

// Per-camera motion chime toggles (Controls -> Cameras). controlDevices is
// bridge-only, so this lives in controlLayout (owner-writable):
// { enabled: { [deviceDocId]: true } }
export const useCameraAlerts = () => useDoc('controlLayout/_cameraAlerts');

export async function saveCameraAlerts(enabled) {
  await db.collection('controlLayout').doc('_cameraAlerts').set({
    enabled,
    updatedAt: Date.now(),
  });
}

// ISY programs (automations), mirrored + decoded by the bridge into
// controlPrograms. Program mutations ride the same command queue with
// target: 'program' and address = the 4-hex program id.
export const useControlPrograms = () => useCollection('controlPrograms', { orderBy: 'name', dir: 'asc' });

export async function sendProgramCommand(pgmId, cmd, { email } = {}) {
  const ref = await db.collection('controlCommands').add({
    target: 'program',
    address: pgmId,
    cmd, // run | runThen | runElse | stop | enable | disable
    requestedBy: email,
    status: 'pending',
    createdAt: Date.now(),
  });
  return ref.id;
}

// Floorplan positions for the device map — { x, y } in map viewBox percent,
// keyed by controlDevices doc id. Written straight from the UI (drag to place).
export const useControlLayout = () => useCollection('controlLayout');

// Quick-access favorites on the Controls page — an ordered list of
// controlDevices doc ids, owner-curated via the star on each card.
export const useControlFavorites = () => useDoc('controlLayout/_favorites');

// Snooze for the scheduled auto-off sweeps (deck heaters / spa): while
// until > now the backend heal worker skips every offAt slot. Toggled from
// the favorite tiles; controlDevices is bridge-only so this lives in
// controlLayout (owner-writable).
export const useAutoOffSnooze = () => useDoc('controlLayout/_autoOffSnooze');

export async function saveAutoOffSnooze(untilMs, email) {
  await db.collection('controlLayout').doc('_autoOffSnooze').set({
    until: untilMs,
    setBy: email || null,
    updatedAt: Date.now(),
  });
}

export async function saveControlFavorites(ids) {
  await db.collection('controlLayout').doc('_favorites').set({
    ids,
    updatedAt: Date.now(),
  });
}

export async function saveDevicePosition(deviceDocId, x, y) {
  await db.collection('controlLayout').doc(deviceDocId).set({
    x: Math.round(x * 10) / 10,
    y: Math.round(y * 10) / 10,
    updatedAt: Date.now(),
  });
}

// Room-rectangle overrides drawn by the owner in the map's "Edit rooms" mode.
// One doc holding the whole set:
//   zones:  { [zoneId]: {x,y,w,h,label?} }  — overrides for built-in zones
//   custom: { [zoneId]: {x,y,w,h,label,type} } — owner-created rooms/areas
export async function saveZoneLayout(zones, custom = {}) {
  await db.collection('controlLayout').doc('_zones').set({
    zones,
    custom,
    updatedAt: Date.now(),
  });
}

// Site geometry edited on the map: property lines, hillface, retaining wall,
// house structure footprint (arrays of {x,y} vertices — Firestore can't nest
// raw arrays) plus measured reference distances ({x1,y1,x2,y2,ft}) the owner
// reads off Google Earth.
export async function saveSiteLayout({ lot, hill, wall, structure, refs }) {
  await db.collection('controlLayout').doc('_site').set({
    lot,
    hill,
    wall,
    structure,
    refs: refs || [],
    updatedAt: Date.now(),
  });
}
